import Cookies from 'js-cookie';
import router from '@/router';

// Detectar ambiente (verificando o domínio atual)
export const getEnvironment = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname.includes('app.fornecedoresconfeccao.com.br')) {
      return 'production';
    } else if (
      window.location.hostname.includes(
        'app-staging.fornecedoresconfeccao.com.br'
      )
    ) {
      return 'staging';
    }
    return 'development';
  }
  return process.env.NODE_ENV || 'development';
};

const getCookieConfig = () => {
  const env = getEnvironment();

  // Configuração base
  const config = {
    expires: 7, // 7 dias
    secure: env !== 'development',
    sameSite: 'Lax',
    path: '/',
  };

  // Configurar o domínio correto baseado no ambiente
  if (env === 'production') {
    config.domain = '.fornecedoresconfeccao.com.br';
  } else if (env === 'staging') {
    config.domain = '.staging.fornecedoresconfeccao.com.br';
  }
  // Em desenvolvimento, deixar o domínio como undefined para usar o domínio atual

  return config;
};

// Configuração geral para os cookies
const TOKEN_CONFIG = getCookieConfig();

// Chaves para os cookies
const KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
};

// Tamanho máximo do cookie (em bytes)
const MAX_COOKIE_SIZE = 4000; // ~4KB é seguro para a maioria dos navegadores

// Flag para indicar se um refresh está em andamento
let isRefreshingTokens = false;
// Acompanhar a última vez que tentamos fazer refresh para evitar loops infinitos
let lastRefreshAttempt = 0;
// Timer mínimo entre tentativas de refresh (3 segundos)
const MIN_REFRESH_INTERVAL = 3000;

const cookieService = {
  // Tokens de acesso
  setAccessToken(token) {
    Cookies.set(KEYS.ACCESS_TOKEN, token, TOKEN_CONFIG);

    // Backup no localStorage
    try {
      localStorage.setItem('accessToken', token);
    } catch (e) {
      console.warn('[Cookie] Falha ao salvar token no localStorage:', e);
    }
  },

  getAccessToken() {
    let token = Cookies.get(KEYS.ACCESS_TOKEN);

    // Se não encontrar no cookie, tentar recuperar do localStorage
    if (!token) {
      token = localStorage.getItem('accessToken');
      if (token) {
        // Restaurar o cookie
        this.setAccessToken(token);
      }
    }

    return token;
  },

  // Tokens de refresh
  setRefreshToken(token) {
    // Maior duração para o refresh token
    Cookies.set(KEYS.REFRESH_TOKEN, token, {
      ...TOKEN_CONFIG,
      expires: 30, // 30 dias
    });

    // Backup no localStorage
    try {
      localStorage.setItem('refreshToken', token);
    } catch (e) {
      console.warn(
        '[Cookie] Falha ao salvar refresh token no localStorage:',
        e
      );
    }
  },

  getRefreshToken() {
    let token = Cookies.get(KEYS.REFRESH_TOKEN);

    // Se não encontrar no cookie, tentar recuperar do localStorage
    if (!token) {
      token = localStorage.getItem('refreshToken');
      if (token) {
        // Restaurar o cookie
        this.setRefreshToken(token);
      }
    }

    return token;
  },

  // Dados do usuário
  setUserData(userData) {
    try {
      // Garantir que userData.accountRole esteja presente
      if (userData && userData.accountRoleId && !userData.accountRole) {
        userData.accountRole = {
          id: userData.accountRoleId,
          name:
            userData.accountRoleId === '8e56078d-962e-49be-91c7-d510b4b4879e'
              ? 'ADMIN'
              : 'USER',
        };
      }

      const userDataString = JSON.stringify(userData);

      // Verificar o tamanho dos dados para garantir que não excedam o limite
      const dataSize = new Blob([userDataString]).size;

      if (dataSize > MAX_COOKIE_SIZE) {
        console.warn(
          `[Cookie] Dados do usuário (${dataSize} bytes) excedem o tamanho máximo seguro de cookie (${MAX_COOKIE_SIZE} bytes). Removendo dados não essenciais.`
        );

        // Se for muito grande, remover dados não essenciais como históricos, listas grandes, etc.
        const slimUserData = { ...userData };
        if (
          slimUserData.companyMembers &&
          slimUserData.companyMembers.length > 0
        ) {
          slimUserData.companyMembers = [];
        }
        // Adicionar outras propriedades grandes aqui se necessário

        const slimUserDataString = JSON.stringify(slimUserData);

        Cookies.set(KEYS.USER_DATA, slimUserDataString, TOKEN_CONFIG);

        // Também salvar no localStorage
        try {
          localStorage.setItem('userData', slimUserDataString);
        } catch (e) {
          console.warn('[Cookie] Falha ao salvar userData no localStorage:', e);
        }
      } else {
        Cookies.set(KEYS.USER_DATA, userDataString, TOKEN_CONFIG);

        // Também salvar no localStorage
        try {
          localStorage.setItem('userData', userDataString);
        } catch (e) {
          console.warn('[Cookie] Falha ao salvar userData no localStorage:', e);
        }
      }
    } catch (error) {
      console.error('[Cookie] Erro ao salvar dados do usuário:', error);
    }
  },

  getUserData() {
    try {
      let data = Cookies.get(KEYS.USER_DATA);

      // Se não encontrar no cookie, tentar recuperar do localStorage
      if (!data) {
        data = localStorage.getItem('userData');
        if (data) {
          // Restaurar o cookie
          Cookies.set(KEYS.USER_DATA, data, TOKEN_CONFIG);
        } else {
          return null;
        }
      }

      const userData = JSON.parse(data);

      // Verificar se precisamos reconstruir o accountRole a partir do accountRoleId
      if (userData && userData.accountRoleId && !userData.accountRole) {
        userData.accountRole = {
          id: userData.accountRoleId,
          name:
            userData.accountRoleId === '8e56078d-962e-49be-91c7-d510b4b4879e'
              ? 'ADMIN'
              : 'USER',
        };
      }

      return userData;
    } catch (error) {
      console.error('[Cookie] Erro ao recuperar dados do usuário:', error);
      // Em caso de erro, limpa o cookie corrompido
      this.clearAll();
      return null;
    }
  },

  // Limpar todos os cookies
  clearAll() {
    const options = TOKEN_CONFIG;

    // Limpar cookies
    Cookies.remove(KEYS.ACCESS_TOKEN, options);
    Cookies.remove(KEYS.REFRESH_TOKEN, options);
    Cookies.remove(KEYS.USER_DATA, options);

    // Limpar localStorage
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('currentUser');
    } catch (e) {
      console.warn('[Cookie] Erro ao limpar localStorage:', e);
    }
  },

  // Verificar se o usuário está autenticado
  isAuthenticated() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const userData = this.getUserData();

    // Se temos access token, estamos autenticados
    if (accessToken) {
      return true;
    }

    // Se não temos access token, mas temos refresh token e dados do usuário,
    // iniciamos automaticamente o processo de refresh
    if (!accessToken && refreshToken && userData) {
      // Verificar se devemos tentar refresh (evitar loop)
      const now = Date.now();
      if (
        !isRefreshingTokens &&
        now - lastRefreshAttempt > MIN_REFRESH_INTERVAL
      ) {
        lastRefreshAttempt = now;

        // Indica ao usuário que está sendo redirecionado para o login por problema de autenticação
        if (
          router &&
          router.currentRoute &&
          router.currentRoute.value.name !== 'login'
        ) {
          router.push({
            name: 'login',
            query: {
              error: 'refresh_needed',
              redirect: router.currentRoute.value.fullPath,
            },
          });
        }
      }
    }

    return false;
  },

  // Método auxiliar para verificar se temos um refresh token válido
  hasRefreshToken() {
    const refreshToken = this.getRefreshToken();
    return !!refreshToken;
  },

  // Método auxiliar para forçar redirecionamento para login
  redirectToLogin(errorCode = 'session_expired') {
    if (
      router &&
      router.currentRoute &&
      router.currentRoute.value.name !== 'login'
    ) {
      router.push({
        name: 'login',
        query: {
          error: errorCode,
          redirect: router.currentRoute.value.fullPath,
        },
      });
    }
  },
};

export default cookieService;
